// T-170 Fase 1d — sezione "In elaborazione" nella scheda Documenti.
// Polla document_jobs (P3) + mostra il progress dei draft in upload. Step reali;
// failed → Riprova/Annulla; ready → tap per rivedere (review-by-job).

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonSpinner,
  IonButton,
  IonIcon,
  IonListHeader,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonToast,
  IonAlert,
} from '@ionic/react';
import {
  documentTextOutline, refreshOutline, closeCircleOutline, imageOutline, openOutline,
  downloadOutline, shareOutline, expandOutline, createOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share, shareWithNavigator } from '../lib/adapters/share/index.js';
import { ZoomableImage } from './ZoomableImage.js';
import { useDocumentJobs } from '../lib/documents/useDocumentJobs.js';
import { usePageActivity } from '../lib/hooks/usePageActivity.js';
import {
  getActiveDraftProgress,
  subscribeDraftProgress,
  resumeNonTerminalDrafts,
  type DraftProgress,
} from '../lib/documents/draftStore.js';
import { dismissDocumentJob, retryDocumentJob, signStagingPages } from '../lib/documents/documentJobsClient.js';
import { originalPageFromReviewPayload } from '../lib/documents/documentReviewOriginal.js';
import { openExternalUrl } from '../lib/openExternalUrl.js';
import { fireJobReady } from '../lib/notifications/jobNotifier.js';
import { assessDocumentAutoImport, isActiveJobStatus, type DocumentJobRow } from '@trackeat/shared';
import { useAuth } from '../lib/auth/AuthProvider.js';
import { hasAppCapability } from '../lib/auth/capabilities.js';
import {
  approveDocumentImportReview,
  fetchDocumentImportReviewPayload,
  rejectDocumentImportReview,
  type DocumentImportReview,
} from '../lib/documents/documentImportReviews.js';
import { useDocumentImportReviews } from '../lib/documents/useDocumentImportReviews.js';
import { useAccountAccessState } from '../lib/data/queries.js';
import { AiRobotIcon } from './AiRobotIcon.js';
import {
  buildPendingDocumentDismissConfirmation,
  type DocumentCancellationSummary,
} from '../lib/documents/documentCancellationConfirmation.js';
import './DocumentsProcessingSection.css';

function useDraftProgress(): DraftProgress[] {
  return useSyncExternalStore(subscribeDraftProgress, getActiveDraftProgress, getActiveDraftProgress);
}

// T-335 — scarica/condividi il file caricato. Pattern allineato a
// DocumentDetailPage: nativo → Filesystem + Share sheet; web/PWA → anchor
// download / navigator.share.
async function urlToBase64(url: string): Promise<{ base64: string; blob: Blob }> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`download_failed_${resp.status}`);
  const blob = await resp.blob();
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return { base64, blob };
}

function fileNameFor(path: string, isPdf: boolean, idx: number): string {
  const ext = isPdf ? 'pdf' : 'jpg';
  return `documento_${idx + 1}_${path.split('/').pop()?.split('.')[0] ?? 'file'}.${ext}`;
}

/** Salva il file sul dispositivo (Download/Documenti su nativo, anchor su web). */
async function downloadFile(url: string, filename: string): Promise<string> {
  if (Capacitor.isNativePlatform()) {
    const { base64 } = await urlToBase64(url);
    await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Documents });
    return 'Salvato in Documenti';
  }
  const { blob } = await urlToBase64(url);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objUrl);
  return 'Download avviato';
}

/** Apre la share sheet di sistema per scegliere l'app con cui condividere. */
async function shareFile(url: string, filename: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { base64 } = await urlToBase64(url);
    const written = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
    await Share.share({ files: [written.uri], title: filename });
    return;
  }
  // Web/PWA: Web Share API con file se supportata, altrimenti fallback download.
  const { blob } = await urlToBase64(url);
  const file = new File([blob], filename, { type: blob.type });
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] })) {
    await shareWithNavigator({ files: [file], title: filename });
    return;
  }
  await downloadFile(url, filename);
}

function isShareCancellation(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase();
  return msg.includes('cancel') || msg.includes('abort') || msg.includes('dismiss');
}

function supplierNameOf(job: DocumentJobRow): string | null {
  const ex = (job.result?.supplier as { existing?: { name?: string } } | undefined)?.existing;
  const parsed = (job.result?.supplier as { parsed?: { name?: string } } | undefined)?.parsed;
  return ex?.name ?? parsed?.name ?? null;
}

function cancellationSummaryOf(job: DocumentJobRow): DocumentCancellationSummary {
  const rawType = job.result?.document.type;
  const type = rawType === 'fattura'
    || rawType === 'nota_credito'
    || rawType === 'nota_debito'
    || rawType === 'autofattura'
    || rawType === 'ddt'
    ? rawType
    : job.doc_kind_guess === 'fattura' ? 'fattura' : 'ddt';
  return {
    type,
    number: job.result?.document.number ?? null,
    date: job.result?.document.date ?? null,
    supplierName: supplierNameOf(job),
  };
}

function reviewValue(value: unknown, fallback = '—'): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim()) return value.trim();
  return fallback;
}

function hasReviewValue(value: unknown): boolean {
  return (typeof value === 'number' && Number.isFinite(value))
    || (typeof value === 'string' && value.trim().length > 0);
}

export function DocumentsProcessingSection(props: {
  tenantId: string | null | undefined;
  businessId: string | null | undefined;
}): JSX.Element | null {
  const { tenantId, businessId } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const { isActive, capture } = usePageActivity('/documents', tenantId ?? null);
  const { profile } = useAuth();
  const accountAccess = useAccountAccessState();
  const { jobs, refetch } = useDocumentJobs(tenantId, businessId);
  const reviewsQuery = useDocumentImportReviews(tenantId, businessId);
  const reviews = reviewsQuery.data ?? [];
  const canReviewImports = hasAppCapability(profile, 'document.import.review');
  const drafts = useDraftProgress();
  const [notified, setNotified] = useState<Set<string>>(new Set());

  // T-335 — anteprima del file/foto caricato, anche a job "pronto". L'utente
  // spesso fotografa e carica senza avere il cartaceo davanti: deve poter
  // rivedere cosa ha inviato prima di importare.
  const [viewer, setViewer] = useState<
    { loading: boolean; pages: { url: string; isPdf: boolean; path: string }[] } | null
  >(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reviewBusyId, setReviewBusyId] = useState<string | null>(null);
  const [reviewToReject, setReviewToReject] = useState<string | null>(null);
  const [reviewToApprove, setReviewToApprove] = useState<DocumentImportReview | null>(null);
  const [jobToDismiss, setJobToDismiss] = useState<DocumentJobRow | null>(null);
  const [dismissBusyId, setDismissBusyId] = useState<string | null>(null);
  const automaticRouteRef = useRef<string | null>(null);

  // T-553: un job interamente attestato non espone mai lo stato “Pronto”. La
  // pagina di commit viene aperta in modalità automatica e usa la sessione di
  // chi ha caricato il file. I tablet condivisi restano fail-closed: serve la
  // selezione dell'operatore fisico e quindi la review esplicita.
  useEffect(() => {
    if (!isActive) return;
    if (!accountAccess.resolved || accountAccess.isSharedTablet) return;
    const candidate = jobs.find((job) => (
      job.status === 'ready'
      && assessDocumentAutoImport(job.result).eligible
    ));
    if (!candidate || automaticRouteRef.current === candidate.id) return;
    automaticRouteRef.current = candidate.id;
    history.replace(`/documents/capture?jobId=${candidate.id}&automatic=1`);
  }, [accountAccess.isSharedTablet, accountAccess.resolved, history, isActive, jobs]);

  useEffect(() => {
    if (isActive) return;
    setViewer(null);
    setZoomUrl(null);
    setReviewToReject(null);
    setReviewToApprove(null);
    setJobToDismiss(null);
  }, [isActive]);

  const openViewer = async (j: DocumentJobRow | null, review?: DocumentImportReview): Promise<void> => {
    const isCurrent = capture();
    const paths = j?.page_paths ?? [];
    setViewer({ loading: true, pages: [] });
    try {
      let pages = await signStagingPages(paths);
      // T-669: le review possono restare aperte dopo la pulizia dello staging.
      // Il payload finale conserva comunque l'originale ricevuto: usalo solo
      // quando nessuna pagina staged è più firmabile.
      if (pages.length === 0 && review && tenantId) {
        const stored = await fetchDocumentImportReviewPayload({ tenantId, reviewId: review.id });
        const fallback = originalPageFromReviewPayload(stored.request_payload, review.id);
        if (fallback) pages = [fallback];
      }
      if (isCurrent()) setViewer({ loading: false, pages });
    } catch {
      if (isCurrent()) setViewer({ loading: false, pages: [] });
    }
  };

  const handleDownload = async (url: string, filename: string): Promise<void> => {
    try {
      setToast(await downloadFile(url, filename));
    } catch {
      setToast('Download non riuscito');
    }
  };
  const handleShare = async (url: string, filename: string): Promise<void> => {
    try {
      await shareFile(url, filename);
    } catch (err) {
      if (!isShareCancellation(err)) setToast('Condivisione non riuscita');
    }
  };

  // Notifica (best-effort) quando un job diventa ready la prima volta.
  // Foreground=true perché l'utente è qui sulla lista → niente push, solo dedupe.
  useEffect(() => {
    for (const j of jobs) {
      if (
        j.status === 'ready'
        && !assessDocumentAutoImport(j.result).eligible
        && !notified.has(j.id)
      ) {
        void fireJobReady({ jobId: j.id, supplierName: supplierNameOf(j), foreground: true });
        setNotified((s) => new Set(s).add(j.id));
      }
    }
  }, [jobs, notified]);

  // T-170 1d (P4): riprendi i draft non terminali (upload interrotti da
  // kill/background) all'apertura della scheda Documenti + al ritorno in
  // foreground. Cablato qui per non toccare App.tsx (WIP di un'altra chat).
  useEffect(() => {
    void resumeNonTerminalDrafts();
    const h = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void resumeNonTerminalDrafts();
    });
    return () => { void h.then((l) => l.remove()); };
  }, []);

  // Job non terminali da mostrare (queued/analyzing/ready/failed) + draft in upload.
  const visibleReviews = reviews.filter((review) =>
    review.status === 'awaiting_review'
    || (review.status === 'rejected' && review.submitted_by_user_id === profile?.id));
  const pendingReviewJobIds = new Set(
    reviews
      .filter((review) => review.status === 'awaiting_review')
      .flatMap((review) => review.summary.document_job_id ? [review.summary.document_job_id] : []),
  );
  const visibleJobs = jobs.filter(
    (j) => (
      j.status === 'queued'
      || j.status === 'analyzing'
      || j.status === 'ready'
      || j.status === 'failed'
    ) && !pendingReviewJobIds.has(j.id),
  );
  if (visibleJobs.length === 0 && drafts.length === 0 && visibleReviews.length === 0) return null;

  const stepLabel = (j: DocumentJobRow): string => {
    if (j.status === 'queued') return t('documents.processing.step.queued');
    if (j.status === 'analyzing') {
      return j.step === 'matching'
        ? t('documents.processing.step.matching')
        : t('documents.processing.step.analyzing');
    }
    if (j.status === 'ready' && assessDocumentAutoImport(j.result).eligible) {
      return 'Codici riconosciuti · caricamento automatico';
    }
    if (j.status === 'ready') return t('documents.processing.step.ready');
    if (j.status === 'failed') {
      return j.error?.code
        ? t('documents.processing.error.' + j.error.code, { defaultValue: t('documents.processing.step.failed') })
        : t('documents.processing.step.failed');
    }
    return j.status;
  };

  const badgeColor = (j: DocumentJobRow): string => {
    if (j.status === 'ready') return 'success';
    if (j.status === 'failed') return 'danger';
    return 'warning';
  };
  const pendingDismissConfirmation = jobToDismiss
    ? buildPendingDocumentDismissConfirmation(
        cancellationSummaryOf(jobToDismiss),
        jobToDismiss.status === 'failed' ? 'failed' : 'ready',
      )
    : null;

  return (
    <IonList>
      <IonListHeader>
        <IonLabel>{t('documents.processing.title')}</IonLabel>
      </IonListHeader>

      {visibleReviews.length > 0 && (
        <IonListHeader>
          <IonLabel>Revisione documenti</IonLabel>
        </IonListHeader>
      )}
      {visibleReviews.map((review) => {
        const canAct = canReviewImports
          && review.status === 'awaiting_review'
          && review.submitted_by_user_id !== profile?.id;
        const missing = review.summary.missing_foods;
        const reviewJob = review.summary.document_job_id
          ? jobs.find((job) => job.id === review.summary.document_job_id) ?? null
          : null;
        return (
          <IonItem key={`review-${review.id}`} className="documents-processing-review-item">
            <IonIcon
              icon={documentTextOutline}
              slot="start"
              color={review.status === 'rejected' ? 'danger' : 'warning'}
            />
            <IonLabel className="ion-text-wrap documents-processing-review-label">
              <h3>
                {review.doc_kind.toUpperCase()}
                {review.summary.document_number
                  ? ` ${review.summary.document_number}`
                  : ''}
              </h3>
              <p>
                {review.summary.supplier_name || 'Fornitore da verificare'}
                {review.summary.document_date
                  ? ` · ${review.summary.document_date}`
                  : ''}
              </p>
              <p>
                {missing.length} aliment{missing.length === 1 ? 'o' : 'i'} da creare o associare:
                {' '}{missing.slice(0, 3).map((food) => food.name).join(', ')}
                {missing.length > 3 ? '…' : ''}
              </p>
              {review.status === 'rejected' ? (
                <p style={{ color: 'var(--ion-color-danger)' }}>
                  Correzione richiesta: {review.rejection_reason || 'controlla i dati e invia di nuovo.'}
                </p>
              ) : !canAct && (
                <p>Nessun documento o movimento di magazzino è stato ancora creato.</p>
              )}
            </IonLabel>
            <div slot="end" className="documents-processing-review-end">
              <IonBadge color={review.status === 'rejected' ? 'danger' : 'warning'}>
                {review.status === 'rejected' ? 'Da correggere' : 'In revisione'}
              </IonBadge>
              <div className="documents-processing-review-actions">
                {(reviewJob || review.summary.document_job_id) && (
                  <IonButton
                    fill="clear"
                    color="medium"
                    title="Vedi documento originale"
                    aria-label="Apri documento originale"
                    onClick={(event) => {
                      event.stopPropagation();
                      void openViewer(reviewJob, review);
                    }}
                  >
                    <IonIcon icon={imageOutline} slot="icon-only" />
                  </IonButton>
                )}
                {canAct && (
                  <>
                    <IonButton
                      fill="clear"
                      color="primary"
                      disabled={reviewBusyId !== null}
                      title="Apri revisione importazione"
                      aria-label="Apri revisione importazione"
                      onClick={(event) => {
                        event.stopPropagation();
                        setReviewToApprove(review);
                      }}
                    >
                      <IonIcon icon={openOutline} slot="icon-only" />
                    </IonButton>
                    <IonButton
                      fill="clear"
                      color="danger"
                      disabled={reviewBusyId !== null}
                      title="Rifiuta"
                      aria-label="Rifiuta revisione"
                      onClick={(event) => {
                        event.stopPropagation();
                        setReviewToReject(review.id);
                      }}
                    >
                      <IonIcon icon={closeCircleOutline} slot="icon-only" />
                    </IonButton>
                  </>
                )}
              </div>
            </div>
          </IonItem>
        );
      })}

      {drafts.map((d) => (
        <IonItem key={`draft-${d.draftId}`}>
          <IonSpinner name="dots" slot="start" />
          <IonLabel>
            <h3>{t('documents.processing.uploading', { n: d.uploaded, total: d.total })}</h3>
            <p>{t('documents.processing.step.uploading')}</p>
          </IonLabel>
        </IonItem>
      ))}

      {visibleJobs.map((j) => (
        <IonItem
          key={j.id}
          button={j.status === 'ready' && !assessDocumentAutoImport(j.result).eligible}
          detail={j.status === 'ready' && !assessDocumentAutoImport(j.result).eligible}
          onClick={
            j.status === 'ready' && !assessDocumentAutoImport(j.result).eligible
              ? () => history.push(`/documents/capture?jobId=${j.id}`)
              : undefined
          }
        >
          {j.status === 'ready' && assessDocumentAutoImport(j.result).eligible ? (
            <span slot="start" aria-hidden="true" style={{ color: '#5b55d6', display: 'inline-flex' }}>
              <AiRobotIcon size={24} />
            </span>
          ) : isActiveJobStatus(j.status) ? (
            <IonSpinner name="dots" slot="start" />
          ) : (
            <IonIcon icon={documentTextOutline} slot="start" />
          )}
          <IonLabel className="ion-text-wrap">
            <h3>{supplierNameOf(j) ?? t('documents.processing.unknownSupplier')}</h3>
            <p>{stepLabel(j)}</p>
            {j.duplicate ? <p style={{ color: 'var(--ion-color-warning)' }}>{t('documents.processing.duplicateWarning')}</p> : null}
          </IonLabel>
          <IonBadge color={badgeColor(j)} slot="end">
            {j.status === 'ready' && assessDocumentAutoImport(j.result).eligible
              ? 'AI Auto'
              : t('documents.processing.status.' + j.status, { defaultValue: j.status })}
          </IonBadge>
          {/* T-335: vedi la foto/PDF caricato sui documenti "pronti" (e falliti),
              NON su quelli ancora in elaborazione (queued/analyzing). È quando si
              rivede "con calma" prima di importare che serve verificare la foto. */}
          {(j.status === 'ready' || j.status === 'failed') && (j.page_paths?.length ?? 0) > 0 ? (
            <IonButton
              fill="clear"
              color="medium"
              slot="end"
              title="Vedi foto/file caricato"
              onClick={(e) => {
                e.stopPropagation();
                void openViewer(j);
              }}
            >
              <IonIcon icon={imageOutline} slot="icon-only" />
            </IonButton>
          ) : null}
          {j.status === 'failed' ? (
            <>
              <IonButton
                fill="clear"
                slot="end"
                onClick={async (e) => {
                  e.stopPropagation();
                  await retryDocumentJob(j.id);
                  refetch();
                }}
              >
                <IonIcon icon={refreshOutline} slot="icon-only" />
              </IonButton>
              <IonButton
                fill="clear"
                color="medium"
                slot="end"
                title="Rimuovi elaborazione fallita (richiede conferma)"
                disabled={dismissBusyId !== null}
                aria-label="Rimuovi elaborazione fallita"
                onClick={(e) => {
                  e.stopPropagation();
                  setJobToDismiss(j);
                }}
              >
                <IonIcon icon={closeCircleOutline} slot="icon-only" />
              </IonButton>
            </>
          ) : null}
          {/* T-199: un'estrazione "pronta" ma non ancora importata può essere
              annullata (nessun lotto in magazzino → solo dismiss del job). */}
          {j.status === 'ready' && !assessDocumentAutoImport(j.result).eligible ? (
            <IonButton
              fill="clear"
              color="medium"
              slot="end"
              title="Rimuovi documento pronto (richiede conferma)"
              disabled={dismissBusyId !== null}
              aria-label="Rimuovi documento pronto"
              onClick={(e) => {
                e.stopPropagation();
                setJobToDismiss(j);
              }}
            >
              <IonIcon icon={closeCircleOutline} slot="icon-only" />
            </IonButton>
          ) : null}
        </IonItem>
      ))}

      <IonModal
        isOpen={reviewToApprove !== null}
        onDidDismiss={() => {
          if (reviewBusyId === null) setReviewToApprove(null);
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Verifica prima dell’importazione</IonTitle>
            <IonButtons slot="end">
              <IonButton
                disabled={reviewBusyId !== null}
                onClick={() => setReviewToApprove(null)}
              >
                Chiudi
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {reviewToApprove && (() => {
            const reviewLines = reviewToApprove.summary.lines.length > 0
              ? reviewToApprove.summary.lines
              : reviewToApprove.summary.missing_foods.map((food) => ({
                  ...food,
                  is_new: true,
                  food_id: null,
                  batch_lot: null,
                  expiration_date: null,
                }));
            const conformity = reviewToApprove.summary.conformity
              && typeof reviewToApprove.summary.conformity === 'object'
              && !Array.isArray(reviewToApprove.summary.conformity)
              ? reviewToApprove.summary.conformity as Record<string, unknown>
              : null;
            return (
              <div style={{ maxWidth: 640, margin: '0 auto' }}>
                <div style={{
                  padding: 14,
                  border: '1px solid #f59e0b',
                  borderRadius: 12,
                  background: '#fffbeb',
                  color: '#78350f',
                  fontSize: 14,
                  lineHeight: 1.5,
                }}>
                  L’approvazione crea le nuove schede alimento e applica il carico
                  di magazzino in un’unica operazione. Controlla soprattutto unità
                  di misura, quantità risultante e fattore di conversione.
                </div>

                <h2 style={{ fontSize: 18, margin: '20px 0 4px' }}>
                  {reviewToApprove.doc_kind.toUpperCase()}
                  {reviewToApprove.summary.document_number
                    ? ` ${reviewToApprove.summary.document_number}`
                    : ''}
                </h2>
                <p style={{ color: '#64748b', margin: '0 0 16px', fontSize: 14 }}>
                  {reviewToApprove.summary.supplier_name || 'Fornitore da verificare'}
                  {reviewToApprove.summary.document_date
                    ? ` · ${reviewToApprove.summary.document_date}`
                    : ''}
                  {reviewToApprove.summary.document_total != null
                    ? ` · ${reviewValue(reviewToApprove.summary.document_total)} ${reviewToApprove.summary.currency || 'EUR'}`
                    : ''}
                </p>

                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => {
                    setReviewToApprove(null);
                    history.push(`/documents/capture?reviewId=${reviewToApprove.id}`);
                  }}
                >
                  <IonIcon icon={createOutline} slot="start" />
                  Apri e modifica importazione
                </IonButton>

                <h3 style={{ fontSize: 15, margin: '22px 0 10px' }}>
                  Righe da verificare ({reviewLines.length})
                </h3>
                <div style={{ display: 'grid', gap: 10 }}>
                  {reviewLines.map((food, index) => (
                    <div
                      key={`${food.name}-${index}`}
                      style={{
                        border: '1px solid #dbe5f0',
                        borderRadius: 12,
                        padding: '12px 14px',
                        background: '#fff',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                        <strong style={{ color: '#0f172a', fontSize: 15 }}>{food.name}</strong>
                        <IonBadge color={food.is_new ? 'warning' : 'success'}>
                          {food.is_new ? 'Nuovo' : 'Esistente'}
                        </IonBadge>
                      </div>
                      <div style={{ marginTop: 6, color: '#334155', fontSize: 13.5 }}>
                        Documento: {reviewValue(food.source_qty)} {reviewValue(food.source_uom, '')}
                        {' → '}Magazzino: {reviewValue(food.stock_qty)} {reviewValue(food.stock_uom ?? food.proposed_uom, '')}
                      </div>
                      <div style={{ marginTop: 4, color: '#64748b', fontSize: 12.5 }}>
                        Fattore: {reviewValue(food.conversion_factor, '1')}
                        {food.conversion_source
                          ? ` · ${reviewValue(food.conversion_source)}`
                          : ''}
                      </div>
                      {(hasReviewValue(food.batch_lot) || hasReviewValue(food.expiration_date)) && (
                        <div style={{ marginTop: 4, color: '#64748b', fontSize: 12.5 }}>
                          Lotto: {reviewValue(food.batch_lot)} · Scadenza: {reviewValue(food.expiration_date)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 14, color: '#475569', fontSize: 13 }}>
                  Controllo conformità:{' '}
                  <strong>
                    {conformity?.conform === true
                      ? 'conforme'
                      : conformity?.conform === false
                        ? 'non conforme'
                        : 'non indicato'}
                  </strong>
                </div>

                <p style={{ margin: '18px 0 10px', color: '#475569', fontSize: 13, lineHeight: 1.5 }}>
                  Se un dato non è corretto, chiudi e usa Rifiuta indicando la
                  correzione necessaria. Lo staff potrà riaprire il documento.
                </p>
                <IonButton
                  expand="block"
                  color="success"
                  disabled={reviewBusyId !== null}
                  onClick={() => {
                    const review = reviewToApprove;
                    setReviewBusyId(review.id);
                    void approveDocumentImportReview({
                      tenantId: review.tenant_id,
                      reviewId: review.id,
                    }).then(() => {
                      setReviewToApprove(null);
                      setToast('Revisione approvata: documento e magazzino aggiornati insieme.');
                      void reviewsQuery.refetch();
                      refetch();
                    }).catch(() => {
                      setToast('Approvazione non riuscita. La richiesta resta in attesa.');
                    }).finally(() => setReviewBusyId(null));
                  }}
                >
                  {reviewBusyId === reviewToApprove.id
                    ? <IonSpinner name="dots" />
                    : 'Approva, crea alimenti e carica'}
                </IonButton>
              </div>
            );
          })()}
        </IonContent>
      </IonModal>

      <IonModal isOpen={viewer !== null} onDidDismiss={() => setViewer(null)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Documento caricato</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setViewer(null)}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {viewer?.loading ? (
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <IonSpinner name="dots" />
            </div>
          ) : (viewer?.pages.length ?? 0) === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--ion-color-medium)' }}>
              Nessun file disponibile per questo documento.
            </p>
          ) : (
            viewer?.pages.map((p, i) => {
              const filename = fileNameFor(p.path, p.isPdf, i);
              return (
                <div key={i} style={{ marginBottom: 20 }}>
                  {p.isPdf ? (
                    <IonButton expand="block" fill="outline" onClick={() => void openExternalUrl(p.url)}>
                      <IonIcon icon={openOutline} slot="start" />
                      Apri PDF {viewer.pages.length > 1 ? `(pagina ${i + 1})` : ''}
                    </IonButton>
                  ) : (
                    <div
                      style={{ position: 'relative', cursor: 'zoom-in' }}
                      onClick={() => setZoomUrl(p.url)}
                    >
                      <img
                        src={p.url}
                        alt={`Pagina ${i + 1}`}
                        style={{ width: '100%', borderRadius: 8, display: 'block' }}
                      />
                      <IonIcon
                        icon={expandOutline}
                        style={{
                          position: 'absolute', top: 8, right: 8, fontSize: 24, color: '#fff',
                          background: 'rgba(0,0,0,0.5)', borderRadius: 6, padding: 4,
                        }}
                      />
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <IonButton
                      size="small"
                      fill="clear"
                      onClick={() => void handleDownload(p.url, filename)}
                    >
                      <IonIcon icon={downloadOutline} slot="start" />
                      Scarica
                    </IonButton>
                    <IonButton
                      size="small"
                      fill="clear"
                      onClick={() => void handleShare(p.url, filename)}
                    >
                      <IonIcon icon={shareOutline} slot="start" />
                      Condividi
                    </IonButton>
                  </div>
                </div>
              );
            })
          )}
        </IonContent>
      </IonModal>

      {/* T-335: viewer fullscreen con pinch-zoom / pan / doppio-tap. */}
      <IonModal isOpen={zoomUrl !== null} onDidDismiss={() => setZoomUrl(null)}>
        <IonHeader>
          <IonToolbar style={{ '--background': '#000', '--color': '#fff' } as React.CSSProperties}>
            <IonTitle>Zoom</IonTitle>
            <IonButtons slot="end">
              <IonButton style={{ color: '#fff' }} onClick={() => setZoomUrl(null)}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent scrollY={false} style={{ '--background': '#000' } as React.CSSProperties}>
          {zoomUrl ? <ZoomableImage src={zoomUrl} alt="Documento" /> : null}
        </IonContent>
      </IonModal>

      <IonToast
        isOpen={toast !== null}
        message={toast ?? ''}
        duration={2500}
        onDidDismiss={() => setToast(null)}
      />
      <IonAlert
        isOpen={jobToDismiss !== null}
        header={pendingDismissConfirmation?.header ?? ''}
        subHeader={pendingDismissConfirmation?.subHeader ?? ''}
        message={pendingDismissConfirmation?.message ?? ''}
        backdropDismiss={dismissBusyId === null}
        buttons={jobToDismiss ? [
          { text: 'No, torna indietro', role: 'cancel' },
          {
            text: pendingDismissConfirmation?.confirmText ?? 'Sì, rimuovi',
            role: 'destructive',
            handler: () => {
              const job = jobToDismiss;
              setDismissBusyId(job.id);
              void dismissDocumentJob(job.id)
                .then(() => {
                  setToast('Documento rimosso. Il magazzino è rimasto invariato.');
                  refetch();
                })
                .catch(() => setToast('Rimozione non riuscita. Il documento è ancora disponibile.'))
                .finally(() => setDismissBusyId(null));
            },
          },
        ] : []}
        onDidDismiss={() => setJobToDismiss(null)}
      />
      <IonAlert
        isOpen={reviewToReject !== null}
        header="Rifiuta importazione"
        message="Il magazzino resterà invariato. Indica cosa deve correggere lo staff."
        inputs={[{
          name: 'reason',
          type: 'textarea',
          placeholder: 'Motivo del rifiuto',
        }]}
        buttons={[
          { text: 'Annulla', role: 'cancel' },
          {
            text: 'Rifiuta',
            role: 'destructive',
            handler: (value) => {
              const reason = String((value as { reason?: string })?.reason ?? '').trim();
              if (reason.length < 3 || !reviewToReject) {
                setToast('Inserisci un motivo di almeno 3 caratteri.');
                return false;
              }
              const reviewId = reviewToReject;
              setReviewBusyId(reviewId);
              void rejectDocumentImportReview({ reviewId, reason })
                .then(() => {
                  setToast('Richiesta rifiutata. Il magazzino è rimasto invariato.');
                  void reviewsQuery.refetch();
                })
                .catch(() => setToast('Rifiuto non riuscito.'))
                .finally(() => setReviewBusyId(null));
              return true;
            },
          },
        ]}
        onDidDismiss={() => setReviewToReject(null)}
      />
    </IonList>
  );
}
